'use client';

import React, { useState } from 'react';
import { Plus, Search, AlertTriangle, Package, ChevronDown, ChevronUp } from 'lucide-react';
import {
  MOCK_MEDICATIONS,
  MED_STATUS_CONFIG,
  FLOCKS_LIST,
  MEDICATION_INVENTORY,
  MedicationRecord,
} from './healthData';

function MedStatusPill({ status }: { status: string }) {
  const cfg = MED_STATUS_CONFIG[status] ?? { label: status, color: '#6b7280', bg: '#f9fafb' };
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold" style={{ color: cfg.color, backgroundColor: cfg.bg }}>
      {cfg.label}
    </span>
  );
}

function WithdrawalBanner({ medications }: { medications: MedicationRecord[] }) {
  const alerts = medications.filter(m => m.withdrawalAlert && m.status === 'active');
  if (alerts.length === 0) return null;
  return (
    <div className="mb-4 p-3 rounded-xl border border-red-200 bg-red-50 flex items-start gap-3">
      <AlertTriangle size={16} className="text-red-600 flex-shrink-0 mt-0.5" />
      <div className="text-sm">
        <span className="font-semibold text-red-800">Withdrawal Period Active — </span>
        <span className="text-red-700">
          {alerts.map(m => `${m.flock} (${m.medicine}) — birds/eggs must not be sold until ${m.withdrawalClearDate}`).join(' · ')}
        </span>
      </div>
    </div>
  );
}

function AddMedicationModal({ onClose }: { onClose: () => void }) {
  const [form, setForm] = useState({
    flock: '', medicine: '', reason: '', dosage: '',
    administrationMethod: '', startDate: '', durationDays: '',
    prescribedBy: '', administeredBy: '', withdrawalPeriodDays: '0',
    quantityUsed: '', notes: '',
  });
  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }));

  const selectedMed = MEDICATION_INVENTORY.find(m => m.id === form.medicine);
  const selectedFlock = FLOCKS_LIST.find(f => f.id === form.flock);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Record Medication</h2>
            <p className="text-xs text-gray-500 mt-0.5">Track medicine usage and link to inventory</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-400 text-lg font-bold">×</button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-xs font-semibold text-gray-700 mb-1">Flock *</label>
              <select required value={form.flock} onChange={e => set('flock', e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500">
                <option value="">Select flock...</option>
                {FLOCKS_LIST.map(f => (
                  <option key={f.id} value={f.id}>{f.number} — {f.name} ({f.house})</option>
                ))}
              </select>
              {selectedFlock && (
                <p className="text-xs text-gray-400 mt-1">{selectedFlock.farm} · {selectedFlock.currentQty.toLocaleString()} birds</p>
              )}
            </div>

            <div className="col-span-2">
              <label className="block text-xs font-semibold text-gray-700 mb-1">Medicine (from Inventory) *</label>
              <select required value={form.medicine} onChange={e => set('medicine', e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500">
                <option value="">Select medicine...</option>
                {MEDICATION_INVENTORY.map(m => (
                  <option key={m.id} value={m.id}>{m.name} (Stock: {m.currentQty} {m.unit})</option>
                ))}
              </select>
              {selectedMed && (
                <div className="mt-1 flex items-center gap-1.5 text-xs text-green-700">
                  <Package size={11} />
                  <span>Current stock: <strong>{selectedMed.currentQty} {selectedMed.unit}</strong> — will be deducted on save</span>
                </div>
              )}
            </div>

            <div className="col-span-2">
              <label className="block text-xs font-semibold text-gray-700 mb-1">Reason / Diagnosis *</label>
              <input required value={form.reason} onChange={e => set('reason', e.target.value)}
                placeholder="e.g. Respiratory infection, Coccidiosis treatment"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Dosage *</label>
              <input required value={form.dosage} onChange={e => set('dosage', e.target.value)}
                placeholder="e.g. 1ml per litre of water"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Administration Method</label>
              <select value={form.administrationMethod} onChange={e => set('administrationMethod', e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500">
                <option value="">Select method...</option>
                <option>Drinking water</option>
                <option>Feed mixing</option>
                <option>Injection (IM)</option>
                <option>Injection (SC)</option>
                <option>Topical</option>
                <option>Spray</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Start Date *</label>
              <input required type="date" value={form.startDate} onChange={e => set('startDate', e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Duration (days) *</label>
              <input required type="number" min="1" value={form.durationDays} onChange={e => set('durationDays', e.target.value)}
                placeholder="e.g. 5"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Prescribed By</label>
              <input value={form.prescribedBy} onChange={e => set('prescribedBy', e.target.value)}
                placeholder="Dr. Name"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Administered By</label>
              <input value={form.administeredBy} onChange={e => set('administeredBy', e.target.value)}
                placeholder="Farm worker name"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Quantity Used</label>
              <input type="number" min="0" step="0.1" value={form.quantityUsed} onChange={e => set('quantityUsed', e.target.value)}
                placeholder="0"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
              {selectedMed && <p className="text-xs text-gray-400 mt-1">Unit: {selectedMed.unit}</p>}
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Withdrawal Period (days)</label>
              <input type="number" min="0" value={form.withdrawalPeriodDays} onChange={e => set('withdrawalPeriodDays', e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
              {Number(form.withdrawalPeriodDays) > 0 && (
                <p className="text-xs text-amber-600 mt-1 flex items-center gap-1">
                  <AlertTriangle size={10} /> Withdrawal alert will be set for birds/eggs
                </p>
              )}
            </div>

            <div className="col-span-2">
              <label className="block text-xs font-semibold text-gray-700 mb-1">Notes</label>
              <textarea value={form.notes} onChange={e => set('notes', e.target.value)} rows={2}
                placeholder="Additional notes..."
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 resize-none" />
            </div>
          </div>

          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
              Cancel
            </button>
            <button type="submit"
              className="flex-1 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-colors"
              style={{ backgroundColor: 'var(--primary)' }}>
              Save & Deduct Inventory
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function MedicationTab() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const filtered = MOCK_MEDICATIONS.filter(m => {
    const matchSearch = search === '' ||
      m.flock.toLowerCase().includes(search.toLowerCase()) ||
      m.medicine.toLowerCase().includes(search.toLowerCase()) ||
      m.medicationNumber.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'all' || m.status === statusFilter;
    return matchSearch && matchStatus;
  });

  return (
    <div>
      {showModal && <AddMedicationModal onClose={() => setShowModal(false)} />}

      <WithdrawalBanner medications={MOCK_MEDICATIONS} />

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by flock, medicine, record number..."
            className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500">
          <option value="all">All Statuses</option>
          <option value="active">Active</option>
          <option value="completed">Completed</option>
          <option value="discontinued">Discontinued</option>
        </select>
        <button onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-colors"
          style={{ backgroundColor: 'var(--primary)' }}>
          <Plus size={15} /> Record Medication
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100" style={{ backgroundColor: '#f8fafc' }}>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Record #</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Medicine</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Flock / House</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Period</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Qty Used</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Cost (UGX)</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Withdrawal</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.length === 0 && (
                <tr><td colSpan={9} className="text-center py-12 text-gray-400 text-sm">No medication records found.</td></tr>
              )}
              {filtered.map(med => (
                <React.Fragment key={med.id}>
                  <tr className={`hover:bg-gray-50 transition-colors ${med.withdrawalAlert && med.status === 'active' ? 'bg-red-50/20' : ''}`}>
                    <td className="px-4 py-3 font-mono text-xs font-semibold text-gray-700">{med.medicationNumber}</td>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-gray-800 text-xs">{med.medicine}</div>
                      <div className="text-xs text-gray-400">{med.administrationMethod}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-gray-800 text-xs">{med.flock}</div>
                      <div className="text-xs text-gray-400">{med.house} · {med.farm}</div>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-700 whitespace-nowrap">
                      <div>{med.startDate} →</div>
                      <div>{med.endDate} ({med.durationDays}d)</div>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-700">
                      {med.quantityUsed} {med.quantityUnit}
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-gray-800 text-xs">
                      {med.totalCost.toLocaleString()}
                    </td>
                    <td className="px-4 py-3">
                      {med.withdrawalPeriodDays > 0 ? (
                        <div className={`text-xs font-medium ${med.withdrawalAlert ? 'text-red-600' : 'text-gray-500'}`}>
                          {med.withdrawalPeriodDays}d
                          {med.withdrawalClearDate && (
                            <div className="text-xs font-normal">Clear: {med.withdrawalClearDate}</div>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">None</span>
                      )}
                    </td>
                    <td className="px-4 py-3"><MedStatusPill status={med.status} /></td>
                    <td className="px-4 py-3">
                      <button onClick={() => setExpandedId(expandedId === med.id ? null : med.id)}
                        className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors text-gray-400">
                        {expandedId === med.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      </button>
                    </td>
                  </tr>
                  {expandedId === med.id && (
                    <tr className="bg-amber-50/20">
                      <td colSpan={9} className="px-6 py-4">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                          <div>
                            <div className="font-semibold text-gray-700 mb-1">Reason</div>
                            <div className="text-gray-600">{med.reason}</div>
                          </div>
                          <div>
                            <div className="font-semibold text-gray-700 mb-1">Dosage</div>
                            <div className="text-gray-600">{med.dosage}</div>
                          </div>
                          <div>
                            <div className="font-semibold text-gray-700 mb-1">Personnel</div>
                            <div className="text-gray-600">Prescribed: {med.prescribedBy}</div>
                            <div className="text-gray-600">Administered: {med.administeredBy}</div>
                          </div>
                          <div>
                            <div className="font-semibold text-gray-700 mb-1">Inventory Link</div>
                            <div className="flex items-center gap-1 text-green-700">
                              <Package size={11} />
                              <span>{med.inventoryItemName}</span>
                            </div>
                            <div className="text-gray-500">{med.quantityUsed} {med.quantityUnit} deducted</div>
                          </div>
                          {med.notes && (
                            <div className="col-span-2 md:col-span-4">
                              <div className="font-semibold text-gray-700 mb-1">Notes</div>
                              <div className="text-gray-600">{med.notes}</div>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-gray-100">
          <span className="text-xs text-gray-400">{filtered.length} medication record{filtered.length !== 1 ? 's' : ''}</span>
        </div>
      </div>
    </div>
  );
}
